import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, ClipboardCheck, CalendarCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { PageHeader, StatCard, ErrorBanner, EmptyState } from "../../components/app/ui";
import { EASE } from "../../components/landing/shared";

const TYPE_COLORS = {
  "Clear Aligners": "#2563EB",
  "Braces": "#6366F1",
  "Retainer": "#0891B2",
};

export default function ReportsPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/reports/summary");
      setReport(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const byType = report?.by_treatment_type || {};
  const total = report?.total_active_patients || 0;

  return (
    <div data-testid="reports-page">
      <PageHeader testId="reports-header" title="Reports" sub="Live treatment distribution across your clinic." />
      <ErrorBanner message={error} onRetry={load} />

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-8" data-testid="reports-stats">
        <StatCard testId="report-active-patients" icon={Users} label="Active Patients" value={report?.total_active_patients ?? 0} accent="#0066FF" loading={loading} />
        <StatCard testId="report-pending-reviews" icon={ClipboardCheck} label="Reviews Pending" value={report?.reviews_pending ?? 0} accent="#5E2BFF" loading={loading} />
        <StatCard testId="report-completed-reviews" icon={ClipboardCheck} label="Reviews Completed" value={report?.reviews_completed ?? 0} accent="#00F0FF" loading={loading} />
        <StatCard testId="report-followups-done" icon={CalendarCheck} label="Follow-ups Done" value={report?.followups_completed ?? 0} accent="#22C55E" loading={loading} />
      </div>

      <section className="bg-white border border-slate-200/80 rounded-2xl shadow-[0_1px_3px_rgba(15,23,42,0.05)] p-8" data-testid="reports-distribution">
        <h2 className="font-display text-xl tracking-tight text-[#0F172A] mb-7">Active Patients by Treatment</h2>
        {loading ? (
          <div className="space-y-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-10 rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : total === 0 ? (
          <EmptyState testId="reports-empty" title="No active patients" sub="Treatment distribution will appear once patients are active." />
        ) : (
          <div className="space-y-6">
            {Object.entries(byType).map(([type, count], i) => {
              const pct = total ? Math.round((count / total) * 100) : 0;
              const color = TYPE_COLORS[type] || "#2563EB";
              return (
                <div key={type} data-testid={`report-row-${type.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[#0F172A]">{type}</span>
                    <span className="text-sm text-slate-400">
                      <span className="font-semibold text-[#0F172A]">{count}</span> active · {pct}%
                    </span>
                  </div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 1, ease: EASE, delay: 0.15 + i * 0.12 }}
                      className="h-full rounded-full"
                      style={{ background: `linear-gradient(90deg, ${color}, ${color}88)` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
